import { FunctionComponent, useState, useEffect, useMemo } from "react"
import Sidebar from "../features/sidebar/components/sidebar"
import { useTypedSelector, useTypedDispatch } from "../app/store"
import { useSearchParams, useNavigate } from "react-router-dom"
import { logoutUser } from "../features/auth/authThunks"
import zxcvbn from "zxcvbn"
import { useChangePasswordMutation } from "../app/services/meseeknaApiV2"
import { RoleName } from "../features/auth/types"

const AccountManagement: FunctionComponent = () => {
  const user = useTypedSelector((state) => state.authSlice.user)
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [changePassword, { isLoading }] = useChangePasswordMutation()

  // Determine user's role for showing/hiding account features
  const userRoleConfig = useMemo(() => {
    const roles = user?.roles || []
    const isSiteAdmin = roles.some((r) => r.roleId === RoleName.SiteAdmin) || user?.role === RoleName.SiteAdmin
    const isAdmin = roles.some((r) => r.roleId === RoleName.Admin) || user?.role === RoleName.Admin
    const isRecruiter = roles.some((r) => r.roleId === RoleName.Recruiter) || user?.role === RoleName.Recruiter

    // Determine which features to show based on role
    // SiteAdmin & Admin: All features
    // Recruiter: Security, Users, Session (no Billing)
    // Candidate (TeamMember/SimulationUser): Security, Session only (no Billing, no Users)
    return {
      showBilling: isSiteAdmin || isAdmin,
      showUsers: isSiteAdmin || isAdmin || isRecruiter,
    }
  }, [user])

  // Calculate number of visible cards for responsive grid
  const visibleCardCount = useMemo(() => {
    let count = 2 // Security and Session are always shown
    if (userRoleConfig.showBilling) count++
    if (userRoleConfig.showUsers) count++
    return count
  }, [userRoleConfig])

  const handleLogout = () => {
    dispatch(logoutUser())
    navigate("/")
  }

  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordFeedback, setPasswordFeedback] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    if (searchParams.get("showPasswordForm") === "true") {
      setShowPasswordForm(true)
    }
  }, [searchParams])

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage("")
    setSuccessMessage("")

    try {
      const result = await changePassword({
        email: user?.email,
        currentPassword,
        newPassword,
      }).unwrap()

      if (result.code === 200) {
        setSuccessMessage("Password changed successfully")
        setCurrentPassword("")
        setNewPassword("")
        setPasswordStrength(0)
        setPasswordFeedback("")
        setShowPasswordForm(false)
      } else {
        setErrorMessage(result.message || "Failed to change password")
      }
    } catch (error) {
      const err = error as { data?: { message?: string } }
      setErrorMessage(err.data?.message || "Failed to change password")
    }
  }

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value)
    const result = zxcvbn(value)
    setPasswordStrength(result.score)
    setPasswordFeedback(
      result.feedback.warning || result.feedback.suggestions[0] || "",
    )
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
        return "bg-[#ff5050]"
      case 1:
        return "bg-[#faaa32]"
      case 2:
        return "bg-[#ffca4b]"
      case 3:
        return "bg-[#4d86ff]"
      case 4:
        return "bg-[#50d16b]"
      default:
        return "bg-gray-200"
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen flex">
      <Sidebar />
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-gray-900">Account</h1>
            <p className="text-lg text-gray-500 mt-3">
              {userRoleConfig.showBilling
                ? "Manage your account settings, security, and billing preferences."
                : "Manage your account settings and security preferences."}
            </p>
          </div>

          {/* Notifications */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium">{successMessage}</p>
            </div>
          )}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Cards Grid - responsive based on number of visible cards */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${visibleCardCount > 2 ? 'lg:grid-cols-' + Math.min(visibleCardCount, 4) : ''} gap-6 mb-8`}>
            {/* Security Card - Always visible */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-[#4d86ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Security</h3>
              </div>
              <p className="text-base text-gray-500 mb-4">Update your password and security settings.</p>
              <button
                onClick={() => setShowPasswordForm(true)}
                className="w-full px-4 py-2 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors text-sm font-medium">
                Change Password
              </button>
            </div>

            {/* Billing Card - Only for SiteAdmin and Admin */}
            {userRoleConfig.showBilling && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-[#50d16b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Billing</h3>
                </div>
                <p className="text-base text-gray-500 mb-4">Manage your subscription and billing preferences.</p>
                <form
                  action={`${process.env.REACT_APP_API_URL}/saas/subscriptions/customer-portal`}
                  method="POST">
                  <input type="hidden" name="email" value={user?.email} />
                  <button
                    type="submit"
                    className="w-full px-4 py-2 bg-[#50d16b] text-white rounded-lg hover:bg-[#45c060] transition-colors text-sm font-medium">
                    Manage Billing
                  </button>
                </form>
              </div>
            )}

            {/* Users Card - For SiteAdmin, Admin, and Recruiter */}
            {userRoleConfig.showUsers && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-[#8647a1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Users</h3>
                </div>
                <p className="text-base text-gray-500 mb-4">Add, edit, and manage users in your organization.</p>
                <a
                  href="/dashboard/recruiter-admin/users/all"
                  className="block w-full px-4 py-2 bg-[#8647a1] text-white rounded-lg hover:bg-[#763d91] transition-colors text-sm font-medium text-center">
                  Manage Users
                </a>
              </div>
            )}

            {/* Session Card - Always visible */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Session</h3>
              </div>
              <p className="text-base text-gray-500 mb-4">Sign out of your account on this device.</p>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium">
                Log Out
              </button>
            </div>
          </div>

          {/* Password Change Form */}
          {showPasswordForm && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
                <button
                  onClick={() => setShowPasswordForm(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4d86ff] focus:border-[#4d86ff] outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => handleNewPasswordChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4d86ff] focus:border-[#4d86ff] outline-none transition-colors"
                    required
                  />
                  {newPassword.length >= 4 && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full">
                        <div
                          className={`h-full rounded-full transition-all ${getPasswordStrengthColor()}`}
                          style={{ width: `${(passwordStrength + 1) * 20}%` }}
                        />
                      </div>
                      {passwordFeedback && (
                        <p className="mt-1 text-xs text-gray-500">{passwordFeedback}</p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!currentPassword || !newPassword || passwordStrength < 2 || isLoading}
                  className="w-full px-4 py-2 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed">
                  {isLoading ? "Changing Password..." : "Update Password"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AccountManagement
